import {Injectable} from '@angular/core';
import {
  JobsQuery,
  PaginationInput,
  JobQuery,
  JobMainRequestQuery,
  JobScopeAndFeeQuery,
  JobExtraJobsQuery,
  JobBillingPreviewQuery,
  JobBillingPreviewResult,
  JobAmendFeePreviewQuery,
  JobAmendFeePreviewResult,
  JobType,
  ExtraJobType,
  SyncUsersToJobInput,
  SyncCustomersToJobMutation,
  SyncExpertsToJobMutation,
  JobSortKeyInput,
  CreateServiceRequestShortUrlMutation,
  CreateServiceRequestShortUrlInput,
  namedOperations,
  ConfirmBillingMutation,
  ConfirmProfileMutation,
  MarkAsCompleteMutation,
  PrepareDocumentMutation,
  AmendFeeMutation,
  OrderDetailInput,
} from '@generated/graphql';
import {map, Observable} from 'rxjs';
import {PaginationData} from '@shared/types/pagination.type';
import {Job} from '@modules/job/models/job.model'; // Removed OrderDetail
import {paginationDataFactory} from '@shared/factories/pagination.factory';
import {jobFactory} from '@modules/job/factories/job.factory';
import {extraJobFactory} from '@modules/extra-job/factories/extra-job.factory';
import {ExtraJob} from '@modules/extra-job/models/extra-job.model';
import {FindContract} from '@shared/contracts/find.contract';

@Injectable({
  providedIn: 'root',
})
export class JobApi implements FindContract<Job> {
  constructor(
    protected readonly jobsQuery: JobsQuery,
    protected readonly jobQuery: JobQuery,
    protected readonly jobMainRequestQuery: JobMainRequestQuery,
    protected readonly jobScopeAndFeeQuery: JobScopeAndFeeQuery,
    protected readonly jobExtraJobsQuery: JobExtraJobsQuery,
    protected readonly jobBillingPreviewQuery: JobBillingPreviewQuery,
    protected readonly jobAmendFeePreviewQuery: JobAmendFeePreviewQuery,
    protected readonly syncCustomersToJobMutation: SyncCustomersToJobMutation,
    protected readonly syncExpertsToJobMutation: SyncExpertsToJobMutation,
    protected readonly createServiceRequestShortUrlMutation: CreateServiceRequestShortUrlMutation,
    protected readonly confirmBillingMutation: ConfirmBillingMutation,
    protected readonly confirmProfileMutation: ConfirmProfileMutation,
    protected readonly markAsCompleteMutation: MarkAsCompleteMutation,
    protected readonly prepareDocumentMutation: PrepareDocumentMutation,
    protected readonly amendFeeMutation: AmendFeeMutation,
  ) {
  }

  find$(id: string): Observable<Job> {
    return this.jobQuery.fetch(
      {id, withParent: true},
      {fetchPolicy: 'no-cache'},
    ).pipe(
      map((res) => res.data.job ? jobFactory.create(res.data.job as JobType) : null),
    );
  }

  findMainRequest$(id: string): Observable<Job> {
    return this.jobMainRequestQuery.fetch(
      {id},
      {fetchPolicy: 'no-cache'},
    ).pipe(
      map((res) => res.data.job ? jobFactory.create(res.data.job as JobType) : null),
    );
  }

  findScopeAndFee$(id: string): Observable<Job> {
    return this.jobScopeAndFeeQuery.fetch(
      {id},
      {fetchPolicy: 'no-cache'},
    ).pipe(
      map((res) => res.data.job ? jobFactory.create(res.data.job as JobType) : null),
    );
  }

  findExtraJobs$(id: string): Observable<ExtraJob[]> {
    return this.jobExtraJobsQuery.fetch(
      {id},
      {fetchPolicy: 'no-cache'},
    ).pipe(
      map((res) =>
        (res.data.job?.extraJobs ?? [])
          .filter((item): item is NonNullable<typeof item> => Boolean(item))
          .map((item) => extraJobFactory.create(item as ExtraJobType)),
      ),
    );
  }

  getBillingPreview$(id: string): Observable<JobBillingPreviewResult['jobBillingPreview']> {
    return this.jobBillingPreviewQuery.fetch(
      {id},
      {fetchPolicy: 'no-cache'},
    ).pipe(
      map((res) => res.data.jobBillingPreview),
    );
  }

  getAmendFeePreview$(id: string, services: OrderDetailInput[]): Observable<JobAmendFeePreviewResult['jobAmendFeePreview']> {
    return this.jobAmendFeePreviewQuery.fetch(
      {id, services},
      {fetchPolicy: 'no-cache'},
    ).pipe(
      map((res) => res.data.jobAmendFeePreview),
    );
  }

  syncCustomersToJob$(input: SyncUsersToJobInput): Observable<Job> {
    return this.syncCustomersToJobMutation.mutate({input}, {refetchQueries: [namedOperations.Query.Job]}).pipe(
      map((res) =>
        res.data.syncCustomersToJob
          ? jobFactory.create(res.data.syncCustomersToJob as JobType)
          : null,
      ),
    );
  }

  syncExpertsToJob$(input: SyncUsersToJobInput): Observable<Job> {
    return this.syncExpertsToJobMutation.mutate({input}, {refetchQueries: [namedOperations.Query.Job]}).pipe(
      map((res) =>
        res.data.syncExpertsToJob
          ? jobFactory.create(res.data.syncExpertsToJob as JobType)
          : null,
      ),
    );
  }

  getJobs$(
    pagination: PaginationInput,
    searchText: string = '',
    sorts: JobSortKeyInput[] = [],
  ): Observable<PaginationData<Job>> {
    return this.jobsQuery.fetch(
      {
        pagination,
        searchText,
        sorts,
      },
      {
        fetchPolicy: 'no-cache',
      },
    )
      .pipe(
        map((res) => paginationDataFactory.create(res.data.jobs, jobFactory)),
      );
  }

  createServiceRequestShortUrl$(
    input: CreateServiceRequestShortUrlInput,
  ): Observable<string> {
    return this.createServiceRequestShortUrlMutation.mutate({input}).pipe(map((res) => res.data.createServiceRequestShortUrl));
  }

  confirmBilling$(
    id: string,
  ): Observable<Job> {
    return this.confirmBillingMutation.mutate({id}).pipe(
      map((res) => jobFactory.create(res.data.confirmBilling as JobType)),
    );
  }

  confirmProfile$(
    id: string,
  ): Observable<Job> {
    return this.confirmProfileMutation.mutate({id}).pipe(
      map((res) => jobFactory.create(res.data.confirmProfile as JobType)),
    );
  }

  prepareDocument$(
    id: string,
  ): Observable<Job> {
    return this.prepareDocumentMutation.mutate({id}).pipe(
      map((res) => jobFactory.create(res.data.prepareDocument as JobType)),
    );
  }

  markAsComplete$(
    id: string,
  ): Observable<Job> {
    return this.markAsCompleteMutation.mutate({id}).pipe(
      map((res) => jobFactory.create(res.data.markAsComplete as JobType)),
    );
  }

  amendFee$(
    id: string,
    services: OrderDetailInput[],
  ): Observable<Job> {
    return this.amendFeeMutation.mutate({id, services}).pipe(
      map((res) => jobFactory.create(res.data.amendFee as JobType)),
    );
  }
}
